import { asPlannedTrialId, ENVELOPE_DURATION_CAP_MINUTES } from "@repo/domain";
import { z } from "zod";
import { memberIdSchema, unitSchema } from "./common.schemas";
import { newCanonicalSchema } from "./entity-id.schemas";

const canonicalUrlSchema = z
  .string()
  .url()
  .refine((value) => !value.includes("|"), {
    message: "Catalog references must identify a lineage, not a pinned revision",
  });

export const parameterAnswerValueSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("Quantity"), value: z.number().finite(), unit: unitSchema.optional() }),
  z.object({ kind: z.literal("Integer"), value: z.number().int() }),
  z.object({
    kind: z.literal("Choice"),
    selected: z.array(memberIdSchema),
    freeText: z.string().trim().min(1).optional(),
  }),
  z.object({ kind: z.literal("Boolean"), value: z.boolean() }),
  z.object({ kind: z.literal("Text"), value: z.string() }),
]);

export const parameterAnswerSchema = z.object({
  source: z.union([z.literal("self"), memberIdSchema]),
  parameterId: memberIdSchema,
  value: parameterAnswerValueSchema,
});

// Any UUID, not newEntityIdSchema: a replace list carries the ids trials were
// planned under, and seeded protocols name theirs as UUIDv5 so a reseed keeps them.
export const plannedTrialsSchema = z
  .array(z.object({ id: z.string().uuid().transform(asPlannedTrialId), values: z.array(parameterAnswerSchema) }))
  .refine((trials) => new Set(trials.map((trial) => trial.id)).size === trials.length, {
    message: "Planned trial ids must be unique",
  });

const protocolOccurrenceSchema = z.object({
  id: memberIdSchema.optional(),
  definition: canonicalUrlSchema,
  qualifier: z.string().trim().min(1).optional(),
  parameterValues: z.array(parameterAnswerSchema),
  plannedTrials: plannedTrialsSchema.optional(),
  observationLevels: z.array(z.object({ observationId: memberIdSchema, level: z.literal("trial") })).optional(),
});

const protocolStepSchema = z.object({
  placement: z.object({
    offsetMinutes: z.number().int().nonnegative().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
    durationMinutes: z.number().int().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES).optional(),
  }),
  title: z.string().trim().min(1).optional(),
  occurrences: z.array(protocolOccurrenceSchema).min(1),
});

const protocolDefinitionContentShape = {
  envelopeMinutes: z.number().int().positive().multipleOf(5).max(ENVELOPE_DURATION_CAP_MINUTES),
  steps: z.array(protocolStepSchema),
  exams: z.array(canonicalUrlSchema),
};

export const defineProtocolDefinitionInputSchema = z.object({
  ...protocolDefinitionContentShape,
  definitionUrl: newCanonicalSchema,
  name: z.string().trim().min(1),
  description: z.string().trim().min(1).optional(),
});
export type DefineProtocolDefinitionInput = z.infer<typeof defineProtocolDefinitionInputSchema>;
export type DefineProtocolDefinitionParams = z.input<typeof defineProtocolDefinitionInputSchema>;

export const reviseProtocolDefinitionInputSchema = z.object({
  ...protocolDefinitionContentShape,
  definitionUrl: canonicalUrlSchema,
  expectedVersion: z.number().int().positive(),
});
export type ReviseProtocolDefinitionInput = z.infer<typeof reviseProtocolDefinitionInputSchema>;
export type ReviseProtocolDefinitionParams = z.input<typeof reviseProtocolDefinitionInputSchema>;

export const correctProtocolDefinitionInputSchema = z.object({
  definitionUrl: canonicalUrlSchema,
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).nullable().optional(),
});
export type CorrectProtocolDefinitionInput = z.infer<typeof correctProtocolDefinitionInputSchema>;

export const changeProtocolDefinitionAvailabilityInputSchema = z.object({ definitionUrl: canonicalUrlSchema });
export type ChangeProtocolDefinitionAvailabilityInput = z.infer<typeof changeProtocolDefinitionAvailabilityInputSchema>;

export const clearProtocolValuesInputSchema = z.object({
  definitionUrl: canonicalUrlSchema,
  expectedVersion: z.number().int().positive(),
  values: z
    .array(
      z.object({
        occurrenceId: memberIdSchema,
        plannedTrialId: z.string().uuid().transform(asPlannedTrialId).optional(),
        source: z.union([z.literal("self"), memberIdSchema]),
        parameterId: memberIdSchema,
      }),
    )
    .min(1),
});
export type ClearProtocolValuesInput = z.infer<typeof clearProtocolValuesInputSchema>;
